import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';
import { identityApi } from '../api/identityApi'; // Import identityApi client
import { logError } from '../utils/logging'; // Import logError

function ResetPassword() {
  const { resetToken } = useParams();
  const navigate = useNavigate();

  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setStatus('submitting');
    setError('');

    try {
      await identityApi.resetPassword(resetToken, password, confirmPassword);
      
      setStatus('success');
      setSuccess('Password reset successfully! Redirecting to login...');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      setStatus('error');
      setError(err.message);
      logError(err, { context: 'ResetPassword handleSubmit' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      }}
    >
      {/* Background gradient overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Container maxWidth="sm" sx={{ mt: 4, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Reset Your Password
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }} align="center">
          Please enter your new password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-test-id="reset-password-error">
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} data-test-id="reset-password-success">
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} data-test-id="reset-password-form">
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={status === 'submitting'}
            inputProps={{ 'data-test-id': 'reset-password-password' }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            disabled={status === 'submitting'}
            inputProps={{ 'data-test-id': 'reset-password-confirm' }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3 }}
            disabled={status === 'submitting'}
            data-test-id="reset-password-submit"
          >
            {status === 'submitting' ? 'Resetting password...' : 'Reset Password'}
          </Button>
        </Box>
      </Paper>
    </Container>
    </Box>
  );
}

export default ResetPassword;